/* Лента вариантов товарных экранов (BotCore.styleRail / railCard, APP-SPEC-5 §1.2, §11 п. 3, §13.1 п. 3, §14 п. 3):
   карточка — превью вашего товара (или темы поста) в варианте, название, метка «Хит» / «Новое», кольцо и галочка у выбранного;
   нажатие выбирает, удержание (или «↗») открывает карточку варианта; в конце «Все N» — лист-сетка с поиском, вкладками и темами.
   Варианты — только опубликованные значения поля формы; подписи — из словаря экрана. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { StudioSheet } from '../studio/StudioSheet';
import type { Choice } from './product-model';

export interface RailVariant {
  value: string;
  choice: Choice;
}
const check = (
  <span className="ck" aria-hidden="true">
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="m3 8 3 3 7-7" />
    </svg>
  </span>
);
export function ProductVariantRail({
  title,
  allTitle,
  variants,
  value,
  disabled,
  onChange,
  preview,
  themes = [],
  size = 'md',
  aboutLines,
}: {
  title: string;
  /** «Все оформления», «Все стили», «Все наборы» */
  allTitle: string;
  variants: RailVariant[];
  value: string;
  disabled?: boolean;
  onChange(value: string): void;
  /** Картинка варианта: товар или слайд-крючок в оформлении */
  preview(variant: RailVariant): ReactNode;
  themes?: string[];
  size?: 'md' | 'sm';
  /** Строки «Об оформлении» в карточке варианта */
  aboutLines?: (variant: RailVariant) => string[];
}) {
  const [sheet, setSheet] = useState(false);
  const [peek, setPeek] = useState<RailVariant | null>(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'all' | 'pop' | 'new'>('all');
  const [theme, setTheme] = useState('');
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);
  const fromSheet = useRef(false);
  useEffect(() => () => clearTimeout(hold.current), []);
  // В ленте восемь первых (порядок формы — порядок популярности) и выбранный, если он дальше (§1.2).
  const rail = variants.slice(0, 8);
  const picked = variants.find(item => item.value === value);
  /* выбранный в листе вариант, которого нет в ленте, встаёт первым — выбор всегда виден (styleRail шаблона) */
  if (picked && !rail.includes(picked)) rail.unshift(picked);
  const choose = (next: string) => {
    onChange(next);
    setPeek(null);
    setSheet(false);
  };
  const inspect = (variant: RailVariant) => {
    fromSheet.current = sheet;
    setSheet(false);
    setPeek(variant);
  };
  const card = (variant: RailVariant) => {
    const on = variant.value === value;
    return (
      <button
        key={variant.value}
        type="button"
        className={`bcs-c px-c${size === 'sm' ? ' mc-c' : ''}${on ? ' on' : ''}`}
        aria-pressed={on}
        aria-label={uiText(variant.choice.name)}
        disabled={disabled}
        onPointerDown={() => {
          held.current = false;
          clearTimeout(hold.current);
          hold.current = setTimeout(() => {
            held.current = true;
            inspect(variant);
          }, 450);
        }}
        onPointerUp={() => clearTimeout(hold.current)}
        onPointerLeave={() => clearTimeout(hold.current)}
        onPointerCancel={() => clearTimeout(hold.current)}
        onContextMenu={event => {
          event.preventDefault();
          inspect(variant);
        }}
        onClick={event => {
          if (event.detail === 0 || !held.current) choose(variant.value);
        }}
      >
        <span className="pc">
          {preview(variant)}
          {variant.choice.badge && <span className="nb">{uiText(variant.choice.badge)}</span>}
          {on && check}
        </span>
        <b>{uiText(variant.choice.name)}</b>
      </button>
    );
  };
  const needle = query.trim().toLocaleLowerCase().replace(/ё/g, 'е');
  const filtered = variants.filter(
    (variant, index) =>
      (!needle || `${variant.choice.name} ${variant.choice.sub ?? ''}`.toLocaleLowerCase().replace(/ё/g, 'е').includes(needle)) &&
      (tab !== 'pop' || index < 8 || variant.choice.badge === 'Хит') &&
      (tab !== 'new' || variant.choice.badge === 'Новое') &&
      (!theme || variant.choice.themes?.includes(theme)),
  );
  if (!variants.length) return null;
  return (
    <section className="bcs px-rail">
      <div className="bcs-h">
        <b>{uiText(title)}</b>
        <button type="button" className="lnk" onClick={() => setSheet(true)}>
          {uiFormat('Все {0}', variants.length)} ›
        </button>
      </div>
      <div className={`bcs-r px-r${size === 'sm' ? ' mc-r' : ''}`}>
        {rail.map(card)}
        {variants.length > rail.length && (
          <button type="button" className="bcs-c px-c more" onClick={() => setSheet(true)}>
            <span className="pc">
              <span className="bcs-mi">
                <b>{variants.length}</b>
              </span>
            </span>
            <b>{uiFormat('Все {0}', variants.length)}</b>
          </button>
        )}
      </div>
      {sheet && (
        <StudioSheet title={allTitle} onClose={() => setSheet(false)}>
          <h2>{uiText(allTitle)}</h2>
          {variants.length > 12 && (
            <input
              type="search"
              className="px-search"
              aria-label={uiText('Поиск')}
              placeholder={uiText('Найти')}
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          )}
          <div className="seg sm" role="tablist" aria-label={uiText('Вкладки')}>
            {(
              [
                ['all', 'Все'],
                ['pop', 'Популярные'],
                ['new', 'Новые'],
              ] as const
            ).map(([key, name]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
                {uiText(name)}
              </button>
            ))}
          </div>
          {themes.length > 0 && (
            <div className="chips px-themes">
              {themes.map(item => (
                <button
                  key={item}
                  type="button"
                  className={`chip${theme === item ? ' on' : ''}`}
                  aria-pressed={theme === item}
                  onClick={() => setTheme(theme === item ? '' : item)}
                >
                  {uiText(item)}
                </button>
              ))}
            </div>
          )}
          <div className="px-grid3">{filtered.map(card)}</div>
          {!filtered.length && (
            <p className="px-empty">
              <b>{uiText('Ничего не нашлось')}</b> {uiText('Попробуйте другое слово или сбросьте тему')}
            </p>
          )}
          <p className="hint">{uiText('Нажмите, чтобы выбрать · Удерживайте, чтобы посмотреть примеры')}</p>
        </StudioSheet>
      )}
      {peek && (
        <StudioSheet
          title={peek.choice.name}
          onClose={() => {
            setPeek(null);
            if (fromSheet.current) setSheet(true);
          }}
        >
          <h2>{uiText(peek.choice.name)}</h2>
          <div className="px-peek">{preview(peek)}</div>
          {peek.choice.sub && <p>{uiText(peek.choice.sub)}</p>}
          {aboutLines?.(peek).map(line => (
            <p key={line} className="hint">
              {uiText(line)}
            </p>
          ))}
          <button type="button" className="btn" disabled={disabled} onClick={() => choose(peek.value)}>
            {uiFormat('Выбрать «{0}»', uiText(peek.choice.name))}
          </button>
        </StudioSheet>
      )}
    </section>
  );
}
